import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { environment } from '../../../environments/environment';
import {
  Release,
  CreateReleaseRequest,
  UpdateReleaseRequest,
  UpdateAvailable,
  RecordDownloadRequest,
  ReleaseDownload,
  ReleaseDownloadFilter,
  ReleaseDownloadPage,
  ReleaseDownloadStats
} from '../../shared/models/release.model';

@Injectable({
  providedIn: 'root'
})
export class ReleaseService {
  private readonly apiUrl = `${environment.apiUrl}/releases`;

  private readonly http = inject(HttpClient);

  getReleases(): Observable<Release[]> {
    return this.http.get<Release[]>(this.apiUrl);
  }

  getRelease(id: string): Observable<Release> {
    return this.http.get<Release>(`${this.apiUrl}/${id}`);
  }

  createRelease(release: CreateReleaseRequest): Observable<Release> {
    return this.http.post<Release>(this.apiUrl, release);
  }

  createReleaseWithFile(formData: FormData): Observable<Release> {
    return this.http.post<Release>(`${this.apiUrl}/with-file`, formData);
  }

  updateRelease(id: string, release: UpdateReleaseRequest): Observable<Release> {
    return this.http.put<Release>(`${this.apiUrl}/${id}`, release);
  }

  deleteRelease(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  publishRelease(id: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/publish`, {});
  }

  unpublishRelease(id: string): Observable<{ message: string }> {
    return this.http.post<{ message: string }>(`${this.apiUrl}/${id}/unpublish`, {});
  }

  checkForUpdates(currentVersion: string): Observable<UpdateAvailable> {
    return this.http.get<UpdateAvailable>(`${this.apiUrl}/check-update`, {
      params: { currentVersion }
    });
  }

  recordDownload(request: RecordDownloadRequest): Observable<ReleaseDownload> {
    return this.http.post<ReleaseDownload>(`${this.apiUrl}/downloads/record`, request);
  }

  getDownloads(filter?: ReleaseDownloadFilter): Observable<ReleaseDownloadPage> {
    let params: any = {};
    if (filter) {
      if (filter.page) params.page = filter.page.toString();
      if (filter.pageSize) params.pageSize = filter.pageSize.toString();
      if (filter.version) params.version = filter.version;
      if (filter.source) params.source = filter.source;
      if (filter.search) params.search = filter.search;
      if (filter.startDate) {
        params.startDate = filter.startDate instanceof Date ? filter.startDate.toISOString() : filter.startDate;
      }
      if (filter.endDate) {
        params.endDate = filter.endDate instanceof Date ? filter.endDate.toISOString() : filter.endDate;
      }
    }
    return this.http.get<ReleaseDownloadPage>(`${this.apiUrl}/downloads`, { params });
  }

  getDownloadStats(): Observable<ReleaseDownloadStats> {
    return this.http.get<ReleaseDownloadStats>(`${this.apiUrl}/downloads/stats`);
  }

  exportDownloadsCsv(filter?: ReleaseDownloadFilter): Observable<Blob> {
    let params: any = {};
    if (filter) {
      if (filter.version) params.version = filter.version;
      if (filter.source) params.source = filter.source;
      if (filter.search) params.search = filter.search;
      if (filter.startDate) {
        params.startDate = filter.startDate instanceof Date ? filter.startDate.toISOString() : filter.startDate;
      }
      if (filter.endDate) {
        params.endDate = filter.endDate instanceof Date ? filter.endDate.toISOString() : filter.endDate;
      }
    }
    return this.http.get(`${this.apiUrl}/downloads/export.csv`, {
      params,
      responseType: 'blob'
    });
  }

  formatFileSize(bytes: number): string {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }
}
